import { SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { BaseModel, Image } from '.';
import { CustomStepCategoryEnum, OfferIntents, OfferTypes, StepCategory } from './funnel';
import { ActionType } from './funnel-action';
import { defaultThemeColor, greyFontColor, transparent, whiteColor } from './style';

export interface Breadcrumb {
  label: string;
  url?: any[];
}

export enum TemplateCategory {
  Downsell,
  Upsell,
  Survey,
  Support,
}

export enum StepElements {
  Name = 'name',
  Banner = 'banner',
  Body = 'body',
  Media = 'media',
  Buttons = 'buttons',
  Response = 'response',
  Discount = 'discount',
  DiscountSettings = 'discountSettings',
  Exchangeables = 'exchangeables',
  Actions = 'actions',
  SurveyOptions = 'surveyOptions',
  TimeDelay = 'timeDelay',
  TimeOptions = 'timeOptions',
  AdvancedSettings = 'advancedSettings',
  Layout = 'layout',
}

export const StepCategoryDefaultActions: {
  [key in CustomStepCategoryEnum]?: ActionType[];
} = {
  [CustomStepCategoryEnum.CancelOrderSurvey]: [ActionType.Cancel],
  [CustomStepCategoryEnum.CancelSubSurvey]: [ActionType.Cancel],
  [CustomStepCategoryEnum.CancelTrialSurvey]: [ActionType.Cancel],
  [CustomStepCategoryEnum.ReturnSurvey]: [ActionType.RMA],
  [CustomStepCategoryEnum.FixedAmountDownsell]: [ActionType.Discount],
  [CustomStepCategoryEnum.PercentageDownsell]: [ActionType.Discount],
  [CustomStepCategoryEnum.PauseSub]: [ActionType.BillingCycle, ActionType.Save],
  [CustomStepCategoryEnum.ExtendTrial]: [ActionType.BillingCycle, ActionType.Save],
  [CustomStepCategoryEnum.OfferDetails]: [ActionType.Save],
  [CustomStepCategoryEnum.LossAversion]: [ActionType.Save],
  [CustomStepCategoryEnum.Testimonial]: [ActionType.Save],
  [CustomStepCategoryEnum.RefundOrder]: [ActionType.Refund],
  [CustomStepCategoryEnum.DiscountFutureOrder]: [ActionType.Discount],
  [CustomStepCategoryEnum.ReturnOrderRMA]: [ActionType.Refund],
  [CustomStepCategoryEnum.ReviseOrder]: [ActionType.ChangeProduct, ActionType.Save],
  [CustomStepCategoryEnum.ChangeVariant]: [ActionType.ChangeVariant, ActionType.Save],
  [CustomStepCategoryEnum.UpsellOrder]: [ActionType.AddProduct],
  [CustomStepCategoryEnum.ReActivateSubscriptionWithDiscount]: [ActionType.Reactivate],
  [CustomStepCategoryEnum.AdjustShippingFrequency]: [ActionType.BillingCycle, ActionType.Save],
  [CustomStepCategoryEnum.PauseSurvey]: [ActionType.BillingCycle, ActionType.Save],
  [CustomStepCategoryEnum.AdjustTrialFrequency]: [ActionType.BillingCycle],
  [CustomStepCategoryEnum.BillTrialNow]: [ActionType.BillingCycle],
};

export enum ActionHoldoffType {
  NoDelay = 0,
  BrandSetting = null,
  Custom = 1,
}

export enum StepTableFilters {
  None,
  Name,
  Type,
  LastModified,
  DateCreated,
}

export enum StepSortableColumns {
  Name = 'name',
  LastModified = 'lastModified',
}

export enum TableSortDirection {
  Asc = 'asc',
  Desc = 'desc',
}

export interface StepsSortConfig {
  column: StepSortableColumns;
  direction: TableSortDirection;
}

export enum StepStatuses {
  Enabled,
  Disabled,
  Draft,
  All,
}

export const StepStatusLabels = {
  [StepStatuses.Enabled]: 'Active',
  [StepStatuses.Disabled]: 'Inactive',
  [StepStatuses.Draft]: 'Draft',
  [StepStatuses.All]: 'All',
};

export enum StepTableStatuses {
  Active,
  Inactive,
  Draft,
}

export const StepTableStatusLabels = {
  [StepTableStatuses.Active]: 'Active',
  [StepTableStatuses.Inactive]: 'Inactive',
  [StepTableStatuses.Draft]: 'Draft',
};

export enum StepDateTypes {
  LastModified,
  DateCreated,
}

export const StepTableDateLabels = {
  [StepDateTypes.LastModified]: 'Last Modified',
  [StepDateTypes.DateCreated]: 'Date Created',
};

export enum StepPopupActions {
  Create,
  Update,
  Duplicate,
  Delete,
  CreateTemplate,
  SelectType,
  CloneOffer,
}

export enum StepMediaCategory {
  NoMedia = 0,
  Product = 1,
  Fixed = 2,
  Embed = 3,
}

export enum StepMediaSubCategory {
  Product = 1,
  Tagged = 2,
}

export enum StepFixedMediaCategory {
  Gallery = 0,
  Upload = 1,
  Widget = 2,
}

export enum StepEmulatorDevices {
  Mobile = 'mobile',
  Tablet = 'tablet',
  Desktop = 'desktop',
}

export enum StepEmulatorDeviceWidths {
  Mobile = 430,
  Tablet = 840,
  Desktop = 1000,
}

export enum StepEmulationTypes {
  Preview,
  Response,
}

export const embedURLs = {
  youtube: 'https://www.youtube.com/embed',
  vimeo: 'https://player.vimeo.com/video',
  dailyMotion: 'https://www.dailymotion.com/embed/video',
};

export enum EmbedPlatforms {
  Youtube = 'youtube',
  Vimeo = 'vimeo',
  dailyMotion = 'dailymotion',
}

export const videoFilePattern = /\.(mp4|webm|mov|m4v|ogg|ogv)(?:[?#]|$)/i;

export interface StepTemplate {
  title: string;
  description: string;
  type?: StepCategory;
  icon: string;
  offer_type: OfferTypes;
  offer_intent?: OfferIntents;
  offer_intents?: OfferIntents[];
  disabled?: boolean;
  subtypes?: StepTemplate[];
}

export interface StepTypePopupData {
  is_survey: boolean;
  offer_name: string;
  offer_id: number | string | null;
  offer_intent: OfferIntents | null;
  offer_type: OfferTypes | null;
  popupType: StepPopupActions;
  steps: StepTemplate[];
  use_existing_template: boolean;
}

export enum StepButtonTypes {
  Confirm,
  Reject,
}

export interface StepButtons {
  confirmButton: StepButtonDetails;
  rejectButton: StepButtonDetails;
}

export interface StepButtonDetails {
  content?: string;
  backgroundColor: string;
  borderColor: string;
  fontColor: string;
  borderRadius?: string;
  borderWidth?: string;
  brandBackground: string;
  brandBorder: string;
  brandFont: string;
  style?: StepButtonShapes;
  isBackgroundDisabled?: boolean;
  isBorderDisabled?: boolean;
  isFontDisabled?: boolean;
  type: StepButtonControlTypes;
  shapesPopup: boolean;
  controlPopup: boolean;
  borderWidthPopup?: boolean;
  disabled?: boolean;
}

export enum StepButtonControlTypes {
  Theme,
  Custom,
}

export enum StepButtonShapes {
  Flat,
  Modern,
  Pill,
  FlatOutline,
  ModernOutline,
  PillOutline,
  TextOnly,
}

export const StepButtonShapeStyles = {
  [StepButtonShapes.Flat]: {
    backgroundColor: defaultThemeColor,
    borderColor: whiteColor,
    borderRadius: '0px',
    borderWidth: '1px',
    isBackgroundDisabled: false,
    isBorderDisabled: false,
    isFontDisabled: false,
  },
  [StepButtonShapes.Modern]: {
    backgroundColor: defaultThemeColor,
    borderColor: whiteColor,
    borderRadius: '5px',
    borderWidth: '1px',
    isBackgroundDisabled: false,
    isBorderDisabled: false,
    isFontDisabled: false,
  },
  [StepButtonShapes.Pill]: {
    backgroundColor: defaultThemeColor,
    borderColor: whiteColor,
    borderRadius: '40px',
    borderWidth: '1px',
    isBackgroundDisabled: false,
    isBorderDisabled: false,
    isFontDisabled: false,
  },
  [StepButtonShapes.FlatOutline]: {
    backgroundColor: whiteColor,
    borderColor: defaultThemeColor,
    borderRadius: '0px',
    borderWidth: '1px',
    isBackgroundDisabled: true,
    isBorderDisabled: false,
  },
  [StepButtonShapes.ModernOutline]: {
    backgroundColor: whiteColor,
    borderColor: defaultThemeColor,
    borderRadius: '5px',
    borderWidth: '1px',
    isBorderDisabled: false,
    isBackgroundDisabled: true,
  },
  [StepButtonShapes.PillOutline]: {
    backgroundColor: whiteColor,
    borderColor: defaultThemeColor,
    borderRadius: '40px',
    borderWidth: '1px',
    isBackgroundDisabled: true,
    isBorderDisabled: false,
  },
  [StepButtonShapes.TextOnly]: {
    fontColor: greyFontColor,
    backgroundColor: transparent,
    borderColor: transparent,
    borderRadius: '0px',
    borderWidth: '0px',
    isBackgroundDisabled: true,
    isBorderDisabled: true,
    isFontDisabled: false,
  },
};

export interface StepPreviewButtonDetails {
  label: SafeHtml;
  backgroundColor: string;
  color: string;
  borderRadius: string;
  borderColor: string;
  borderWidth?: string;
}

export interface StepSurveyResponseCategory extends BaseModel {
  category: string;
  reason: string;
  categoryId: string | number;
  troubleshooterEnabled?: boolean;
}

export interface StepResponseScreen {
  detailButton: Omit<
    StepButtonDetails,
    'type' | 'shapesPopup' | 'controlPopup' | 'brandBackground' | 'brandBorder' | 'brandFont'
  >;
  continueButton: Omit<
    StepButtonDetails,
    'type' | 'shapesPopup' | 'controlPopup' | 'brandBackground' | 'brandBorder' | 'brandFont'
  >;
  titleContent: string;
  responseContent: string;
}

export interface StepTextual {
  banner?: string;
  body: StepTextualBody;
}

export interface StepTextualBody {
  label: string;
  subtitle?: string;
  content: string;
}

export enum StepMediaPanels {
  Step = 1,
  Response = 2,
}

export interface StepMedia {
  category: StepMediaCategory;
  subCategory: StepMediaSubCategory;
  fixedImage?: {
    [StepEmulatorDevices.Mobile]?: Image;
    [StepEmulatorDevices.Tablet]?: Image;
    [StepEmulatorDevices.Desktop]?: Image;
    productImage?: Image;
    category: StepFixedMediaCategory;
  };
  embedVideo?: {
    url: string;
    isURLValid: boolean;
    isFile?: boolean;
    sanitizedURL?: SafeResourceUrl | null;
  };
  placeholderImage?: string;
}

export interface StepResponseMedia {
  category: StepMediaCategory;
  subCategory?: StepMediaSubCategory;
  fixedImage?: {
    [StepEmulatorDevices.Mobile]?: Image;
    [StepEmulatorDevices.Tablet]?: Image;
    [StepEmulatorDevices.Desktop]?: Image;
    productImage?: Image;
    category: StepFixedMediaCategory;
  };
  embedVideo?: {
    url: string;
    isURLValid: boolean;
    isFile?: boolean;
    sanitizedURL?: SafeResourceUrl | null;
  };
}

export interface StepProductsPreview {
  data: any;
  loading: boolean;
}

/* Editor Ids */
export enum StepEditorIds {
  Banner = 'bannerEditor',
  Label = 'labelEditor',
  Subtitle = 'subtitleEditor',
  Content = 'contentEditor',
  ConfirmButton = 'confirmButtonEditor',
  RejectButton = 'rejectButtonEditor',
  Response = 'responseEditor',
  ResponseTitle = 'responseTitleEditor',
}

/* Layout Models */
export enum StepElementTypes {
  Textual,
  Media,
  Buttons,
  ResponseScreen,
  SurveyOptions,
  Discount,
  DiscountSettings,
  TimeDelay,
  TimeOptions,
  Exchangeables,
  Actions,
  Cancellation,
  AdvancedSettings,
  Layout,
}

export interface StepLayout {
  type: StepElementTypes;
  layout:
    | StepTextualLayout
    | StepImagesLayout
    | StepResponseScreenLayout
    | StepTimeOptionsLayout
    | StepDiscountSettingsLayout
    | StepDiscountLayout
    | StepButtonsLayout;
}

export interface StepBasicLayout {
  title: string;
  subtitle?: string;
}

export interface StepTextualLayout {
  banner?: StepBasicLayout;
  label: StepBasicLayout;
  content: StepBasicLayout;
}

export interface StepImagesLayout extends StepBasicLayout {
  placeholderImage: string;
  enabled: boolean;
}

export interface StepResponseScreenLayout extends StepBasicLayout {
  responseTitle: StepBasicLayout;
  response: StepBasicLayout;
}

export interface StepTimeOptionsLayout extends StepBasicLayout {
  type: StepTimeOptionTypes;
  items: {
    value: number;
    unit: StepDurationUnits;
  }[];
}

export enum StepTimeOptionTypes {
  ShippingFreq,
  PauseOptions,
}

export interface StepDiscountSettingsLayout extends StepBasicLayout {
  type: StepDiscountSettingTypes;
}

export enum StepDiscountSettingTypes {
  DiscountRefund,
  FutureOrder,
}

export interface StepDiscountLayout extends StepBasicLayout {
  inputTitle?: string;
  type?: StepDiscountTypes;
}

export enum StepDiscountTypes {
  Amount,
  Percentage,
}

export interface StepSurveyOptionsLayout extends StepBasicLayout {
  items: StepSurveyOption[];
}

export interface StepSurveyOption {
  reason: string;
  category: string;
}

export interface StepButtonsLayout extends StepBasicLayout {
  confirmTitle: string;
  rejectTitle?: string;
  rejectDisabled?: boolean;
}

export enum StepDurationUnits {
  Hours,
  Days,
  Weeks,
  Months,
}

export enum StepSectionWidths {
  Banner = 'banner',
  Label = 'label',
  Content = 'content',
  Subtitle = 'subtitle',
  AcceptButtonContent = 'accept_button_content',
  RejectButtonContent = 'reject_button_content',
  StaticImage = 'static_image',
}

export enum ExchangeableTypeOption {
  ProductOptions,
  ProductIndex,
  AdvancedSets,
}

export interface StepTemplateFolder extends BaseModel {
  name: string;
  category: number;
  steps_count: number;
  thumbnails: string[];
  is_global: boolean;
  showThreeDotMenu?: boolean;
}

export const CategoryTitles = {
  [OfferTypes.Downsell]: 'Select Offer Type',
  [OfferTypes.Upsell]: 'Select Offer Type',
  [OfferTypes.Survey]: 'Select Survey Type',
  [OfferTypes.Support]: 'Select Support Type',
  [OfferTypes.CustomerPortal]: 'Select Step Type',
};

export const ChoiceTypeCategories = [
    CustomStepCategoryEnum.CancelOrderSurvey,
    CustomStepCategoryEnum.CancelSubSurvey,
    CustomStepCategoryEnum.CancelTrialSurvey,
    CustomStepCategoryEnum.ReturnSurvey,
    CustomStepCategoryEnum.Survey,
    CustomStepCategoryEnum.PauseSurvey,
    CustomStepCategoryEnum.AdjustShippingFrequency,
  ];

export const StepHeaderDetails = {
  retention: {
    title: 'Create or edit retention offers by customer stage',
    subtitle:
      'Customer Stages show where a consumer is in their order or subscription journey, including fulfillment status. These are default stages. Use Segments for more targeted control by product, billing cycle, or behavior.',
    stageDescription:
      'This group contains the preset save-offers for this stage. You can reorder, enable/disable, edit, or add new offers as needed.',
    segmentStageDescription:
      'Define rule-based segments within a customer stage to personalize retention offers and actions.',
  },
  survey: {
    title: 'Retention Surveys',
    subtitle:
      'Create surveys to understand why customers are leaving. Use their responses to tailor better offers and increase saves.',
  },
  returns: {
    title: 'Manage returns offers by stage',
    subtitle:
      'Customer Stages show where a consumer is in their return journey, including fulfillment status. These are default stages. Use Segments for more targeted control by product, billing cycle, or behavior.',
    stageDescription:
      'This group contains the preset save-offers for this stage. You can reorder, enable/disable, edit, or add new offers as needed.',
    segmentStageDescription:
      'Define rule-based segments within a customer stage to personalize retention offers and actions.',
  },
  resolve: {
    title: 'Resolve Paths',
    subtitle:
      'Create or edit the paths a disputing customer walks to resolve their issue with a refund.',
    stageDescription:
      'This path is built from steps you add here. Reorder, enable/disable, edit, or add steps as needed.',
    segmentStageDescription:
      'Define rule-based segments within a customer stage to personalize resolve paths and actions.',
  },
};

export const RetentionTooltips = {
  customerStages:
    'These stages reflect different points in the customer journey. Select a stage to create your main offer or manage Segments for more targeted experiences.',
  initialOrderSubscription:
    'First subscription order placed and pending fulfillment. The customer has not yet received a product.',
  rebillOrderSubscription: 'Subscription rebill order pending fulfillment for a second billing cycle or later. The customer has previously received at least one order.',
  newOrderOneTime: 'Single purchase order placed and pending fulfillment.',
  newOrderTrial: 'Trial order placed but pending fulfillment.',
  activeSubscription: 'Active subscriptions with no order currently pending.',
  activeTrial: 'Active trial currently in progress.',
};
